import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { EventWithRelationsDto, ResponseDto } from "@mimber/schema"
import { eventsKeys } from "../events.utils"

interface UploadEventImageVariables {
  file: File
}

interface UploadEventImageOptions extends Omit<
  UseMutationOptions<
    ResponseDto<EventWithRelationsDto>,
    Error,
    UploadEventImageVariables
  >,
  "mutationFn"
> {}

export const useUploadEventImage = (
  eventUuid: string,
  options: UploadEventImageOptions = {}
): UseMutationResult<
  ResponseDto<EventWithRelationsDto>,
  Error,
  UploadEventImageVariables
> => {
  guardAppUsage("mimber-organization", "useUploadEventImage")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ file }: UploadEventImageVariables) => {
      const formData = new FormData()
      formData.append("file", file)

      const response = await server.post<ResponseDto<EventWithRelationsDto>>(
        `events/${eventUuid}/image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )

      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: eventsKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: eventsKeys.findOne(eventUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
